import { useEffect, useRef, useState } from "react";
import { ImageOff } from "lucide-react";

/**
 * Resilient image with automatic retry + skeleton/fallback.
 *
 * The same-origin /api/media proxy can transiently fail (502/timeout) when many
 * images load at once on a constrained pod. A plain <img> would then show a
 * broken-image icon until a manual refresh. SmartImage retries a few times with
 * backoff (cache-busting the URL), shows a subtle skeleton while loading, and a
 * neutral fallback if it ultimately fails — so the grid never looks broken.
 */
export const SmartImage = ({
  src,
  alt = "",
  className = "",
  imgClassName = "",
  maxRetries = 2,
  ...rest
}) => {
  const [status, setStatus] = useState(src ? "loading" : "error"); // loading | loaded | error
  const [attempt, setAttempt] = useState(0);
  const retryRef = useRef(null);

  useEffect(() => {
    setStatus(src ? "loading" : "error");
    setAttempt(0);
  }, [src]);

  useEffect(() => () => { if (retryRef.current) clearTimeout(retryRef.current); }, []);

  const handleError = () => {
    if (attempt < maxRetries) {
      const next = attempt + 1;
      retryRef.current = setTimeout(() => {
        setStatus("loading");
        setAttempt(next);
      }, 400 * next);
    } else {
      setStatus("error");
    }
  };

  // Cache-bust retries so the browser does not serve a failed response.
  const finalSrc =
    !src ? null : attempt === 0 ? src : `${src}${src.includes("?") ? "&" : "?"}_r=${attempt}`;

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {finalSrc && status !== "error" && (
        <img
          key={attempt}
          src={finalSrc}
          alt={alt}
          loading="lazy"
          decoding="async"
          onLoad={() => setStatus("loaded")}
          onError={handleError}
          className={`${imgClassName} transition-opacity duration-300 ${status === "loaded" ? "opacity-100" : "opacity-0"}`}
          {...rest}
        />
      )}
      {status !== "loaded" && (
        <div
          aria-hidden="true"
          className={`absolute inset-0 grid place-items-center bg-secondary ${status === "loading" ? "animate-pulse" : ""}`}
        >
          {status === "error" && <ImageOff className="h-6 w-6 text-muted-foreground/40" />}
        </div>
      )}
    </div>
  );
};
